import { useRecordContext, useTranslate } from "ra-core";

import { Card, CardContent } from "@/components/ui/card";
import type { Contact } from "../types";

/** Returns the URL only when it is http(s), so a submitted value can never become a javascript: link. */
export const toSafeHttpUrl = (value: string): string | null => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
};

const fields = [
  ["first_source", "lead_source"],
  ["latest_source", "latest_lead_source"],
  ["campaign", "lead_campaign"],
  ["interest", "product_interest"],
  ["utm_source", "utm_source"],
  ["utm_medium", "utm_medium"],
  ["utm_campaign", "utm_campaign"],
  ["landing_page", "landing_page"],
] as const;

/**
 * "Lead info" panel of the contact page. Renders nothing when the contact has
 * no lead attribution data. All values are rendered as plain text.
 */
export const ContactLeadInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  if (!record) return null;

  const rows = fields.filter(([, source]) => record[source]);
  if (rows.length === 0) return null;

  return (
    <Card className="mt-4">
      <CardContent>
        <h3 className="text-lg font-semibold mb-2">
          {translate("resources.contacts.lead_info.title")}
        </h3>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          {rows.map(([label, source]) => {
            const value = String(record[source]);
            const href =
              source === "landing_page" ? toSafeHttpUrl(value) : null;
            return (
              <div key={source} className="contents">
                <dt className="text-muted-foreground">
                  {translate(`resources.contacts.lead_info.${label}`)}
                </dt>
                <dd className="break-all">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {value}
                    </a>
                  ) : (
                    value
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
};
